import { useEffect, useRef, useState } from "react";
import { TextInput } from "./TextInput";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";

export interface DropdownOption {
  imgSrc?: string;
  label: string;
  value: string;
}

interface DropdownProps {
  label?: string;
  options: DropdownOption[];
  value?: string;
  placeholder?: string;
  error?: string | null;
  disabled?: boolean;
  isRequired?: boolean;
  className?: string;
  onChange?: (value: string) => void;
}

export function Dropdown({
  label,
  options,
  value,
  placeholder = "Select",
  error,
  disabled,
  isRequired,
  className,
  onChange,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (val: string) => {
    onChange?.(val);
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <TextInput
        label={label}
        value={selectedOption?.label || ""}
        placeholder={placeholder}
        error={error || undefined}
        isRequired={isRequired}
        disabled={disabled}
        onClick={() => !disabled && setOpen((p) => !p)}
        readOnly
        faIcon={open ? faChevronUp : faChevronDown}
        faIconClassName={`text-gray-400 p-2 ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
        onButtonClick={() => !disabled && setOpen((p) => !p)}
        inputClassName="px-4 py-2 h-12 cursor-pointer"
      />

      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full bg-[#000000] border border-[#333333] rounded-lg shadow-lg max-h-60 overflow-auto scrollbar-hide">
          {options.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-400">
              No options available
            </div>
          ) : (
            options.map((option) => (
              <div
                key={option.value}
                onClick={() => handleSelect(option.value)}
                className={`px-4 py-3 cursor-pointer text-sm text-white hover:bg-[#1D1D1D] ${value === option.value ? "bg-[#1D1D1D]" : ""
                  }`}
              >
                <div className="flex items-center gap-3">
                  {option.imgSrc && (
                    <img
                      src={option.imgSrc}
                      alt={typeof option.label === "string" ? option.label : "option"}
                      className="w-6 h-6"
                    />
                  )}
                  <span>{option.label}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
